import type { DashboardAssistantAction } from '../services/dashboardAssistantContract';
import type { DashboardDraft } from '../types';
import { clearDashboard, insertGroup } from './groupOperations';
import { deleteWidget, insertWidget, replaceWidget } from './widgetOperations';

/**
 * Applying an assistant's proposal to the draft (SL-1667).
 *
 * Pure, like the rest of `context/`: the draft goes in, a new draft comes out,
 * and `DashboardDraftContext` writes it with **one** `setDraft`. Chaining the
 * context's own setters instead would not work — each reads the draft from its
 * closure, so the second would overwrite the first.
 *
 * **Whole or not at all.** The backend already rejects a proposal with one
 * invalid action (SL-1668), and this side keeps the same rule: a proposal with
 * an action of a type this side does not know, or one that would create an id the
 * dashboard already has, is refused whole. Applying the part that worked would
 * leave the dashboard in a state the user never saw offered.
 *
 * **A target that is gone is not a refusal.** A page or group the proposal names
 * but the draft no longer has — the user edited by hand before applying — falls
 * back to the active one (see `APPLIERS`), and the review shows it there
 * (`assistantPlan`). Only when there is no active one either is it refused.
 *
 * **Clear first, then groups.** A `clear_dashboard` goes before everything, and
 * every `add_group` before the widgets, whatever order the proposal lists them
 * in: what comes after a clear lands on the emptied dashboard, and a widget
 * always finds the group it names — the same grouping the review draws.
 *
 * Nothing here saves anything. The result is an ordinary edit of the draft, so
 * it counts as unsaved like any other (`isDirty` compares drafts) — applying is
 * not saving.
 */

/** Where an action lands when the target it names is no longer on the draft. */
export interface ProposalFallback {
  pageId: string;
  groupId: string;
}

/** What was created or changed, so the canvas can follow and mark it. */
interface ProposalLanded {
  groupIds: string[];
  widgetIds: string[];
}

export type ApplyProposalResult =
  | { ok: true; draft: DashboardDraft; landed: ProposalLanded }
  /** An action of a type this side does not know — the backend proposed something newer. */
  | { ok: false; reason: 'unsupported'; actions: DashboardAssistantAction['type'][] }
  /** An action's target is gone and there was nowhere else to put it. */
  | { ok: false; reason: 'noTarget' }
  /** An action would create an id the dashboard already has — applied already, most likely. */
  | { ok: false; reason: 'duplicate' };

/** Why a single action could not land. */
type ActionRefusal = 'noTarget' | 'duplicate';

interface Step {
  draft: DashboardDraft;
  landed: ProposalLanded;
  /**
   * Where an action whose target is gone lands. It lives in the step and not
   * beside it because a clear changes it: the active group is one of the things
   * a clear removes, and what comes after it lands in the empty group it leaves.
   */
  fallback: ProposalFallback;
  /**
   * The widgets this proposal already took away, by a clear or a delete.
   * Changing or removing one of them again is done, not refused: there is
   * nothing left to touch, and gone is what was asked.
   */
  removed: ReadonlySet<string>;
}

type ActionOf<T extends DashboardAssistantAction['type']> = Extract<
  DashboardAssistantAction,
  { type: T }
>;

/** One applier per action, returning why it could not land when it could not. */
type Applier<T extends DashboardAssistantAction['type']> = (
  step: Step,
  action: ActionOf<T>,
) => Step | ActionRefusal;

const hasPage = (draft: DashboardDraft, pageId: string) =>
  draft.pages.some((page) => page.id === pageId);

const hasGroup = (draft: DashboardDraft, groupId: string) =>
  draft.groups.some((group) => group.id === groupId);

const hasWidget = (draft: DashboardDraft, widgetId: string) =>
  draft.widgets.some((widget) => widget.id === widgetId);

/**
 * The appliers this side has: one per action the backend proposes (SL-1667 the
 * two that create, SL-1668 the three that change, remove and clear).
 *
 * A proposal is computed against the dashboard as it was when it was asked for,
 * and the user may edit by hand before applying it. So a page or group the
 * proposal names but the draft no longer has falls back to the active one,
 * rather than refusing a proposal whose only fault is being a few seconds old.
 */
const APPLIERS: { [T in DashboardAssistantAction['type']]?: Applier<T> } = {
  add_group: (step, action) => {
    if (hasGroup(step.draft, action.group.id)) return 'duplicate';
    const pageId = hasPage(step.draft, action.pageId) ? action.pageId : step.fallback.pageId;
    const draft = insertGroup(step.draft, pageId, action.group);
    if (draft === step.draft) return 'noTarget';

    return {
      ...step,
      draft,
      landed: { ...step.landed, groupIds: [...step.landed.groupIds, action.group.id] },
    };
  },
  add_widget: (step, action) => {
    if (hasWidget(step.draft, action.widget.id)) return 'duplicate';
    const groupId = hasGroup(step.draft, action.groupId) ? action.groupId : step.fallback.groupId;
    const draft = insertWidget(step.draft, groupId, action.widget);
    if (draft === step.draft) return 'noTarget';

    return {
      ...step,
      draft,
      landed: { ...step.landed, widgetIds: [...step.landed.widgetIds, action.widget.id] },
    };
  },
  // A widget the user deleted between the proposal and Apply is not one to
  // recreate or to guess a replacement for: nothing is applied.
  update_widget: (step, action) => {
    if (step.removed.has(action.widgetId)) return step;
    if (!hasWidget(step.draft, action.widgetId)) return 'noTarget';
    return {
      ...step,
      draft: replaceWidget(step.draft, action.widgetId, action.widget),
      // Changed twice, marked once.
      landed: step.landed.widgetIds.includes(action.widgetId)
        ? step.landed
        : { ...step.landed, widgetIds: [...step.landed.widgetIds, action.widgetId] },
    };
  },
  delete_widget: (step, action) => {
    if (step.removed.has(action.widgetId)) return step;
    if (!hasWidget(step.draft, action.widgetId)) return 'noTarget';
    return {
      ...step,
      draft: deleteWidget(step.draft, action.widgetId),
      landed: {
        groupIds: step.landed.groupIds,
        widgetIds: step.landed.widgetIds.filter((id) => id !== action.widgetId),
      },
      removed: new Set([...step.removed, action.widgetId]),
    };
  },
  clear_dashboard: (step) => {
    // With no page there is nowhere to leave the empty group, and nowhere for
    // what follows to land.
    if (step.draft.pages.length === 0) return 'noTarget';
    const { draft, groupOfPage } = clearDashboard(step.draft);
    const pageId = hasPage(draft, step.fallback.pageId) ? step.fallback.pageId : draft.pages[0].id;
    return {
      ...step,
      draft,
      landed: { groupIds: [], widgetIds: [] },
      fallback: { pageId, groupId: groupOfPage[pageId] },
      // A union, so a second clear does not forget what was taken before it.
      removed: new Set([...step.removed, ...step.draft.widgets.map((widget) => widget.id)]),
    };
  },
};

const applierFor = (type: DashboardAssistantAction['type']) =>
  APPLIERS[type] as Applier<DashboardAssistantAction['type']> | undefined;

/** The action types of a proposal this side has no applier for (newer than this build), without repeats. */
export function unsupportedActions(
  actions: DashboardAssistantAction[],
): DashboardAssistantAction['type'][] {
  return Array.from(
    new Set(actions.map((action) => action.type).filter((type) => !applierFor(type))),
  );
}

/** Where an action goes in the order of application: clear, then groups, then the rest. */
const RANK: Partial<Record<DashboardAssistantAction['type'], number>> = {
  clear_dashboard: 0,
  add_group: 1,
};
/** Everything that is neither a clear nor a group: after both. */
const REST_RANK = 2;
const rankOf = (action: DashboardAssistantAction): number => RANK[action.type] ?? REST_RANK;

/** Clear first, then groups, then everything else; each keeps the order it came in. */
const inApplicationOrder = (actions: DashboardAssistantAction[]): DashboardAssistantAction[] =>
  // `sort` is stable, so actions of the same rank keep the proposal's order.
  [...actions].sort((a, b) => rankOf(a) - rankOf(b));

/** Applies every action of a proposal, clear and groups first, or none of them. */
export function applyProposal(
  draft: DashboardDraft,
  actions: DashboardAssistantAction[],
  fallback: ProposalFallback,
): ApplyProposalResult {
  const unsupported = unsupportedActions(actions);
  if (unsupported.length) return { ok: false, reason: 'unsupported', actions: unsupported };

  let step: Step = { draft, landed: { groupIds: [], widgetIds: [] }, fallback, removed: new Set() };
  for (const action of inApplicationOrder(actions)) {
    // Checked again rather than trusted from the pre-pass above, so reordering
    // the two can never turn into a call on `undefined`.
    const apply = applierFor(action.type);
    if (!apply) return { ok: false, reason: 'unsupported', actions: [action.type] };
    const next = apply(step, action);
    if (typeof next === 'string') return { ok: false, reason: next };
    step = next;
  }

  return { ok: true, draft: step.draft, landed: step.landed };
}
